/* eslint-disable no-console */
import { brandConfig, features } from '@/config/brands'

const TAG = '[GPT-Ad]'

const GPT_AD_SLOTS = [
  { name: 'detail1', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail1' },
  { name: 'detail2', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail2' },
  { name: 'detail3', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail3' },
  { name: 'detail4', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail4' },
  { name: 'detail5', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail5' },
  { name: 'detail6', unitPath: '/23357064594/iaa.storyella.top_1072/iaa.storyella.top_1072_detail6' },
]

const SLOT_COUNT = GPT_AD_SLOTS.length
const SIZE = [300, 250] as const
const AD_ENABLED = !!(features?.enableAdSense && brandConfig.texts?.adSenseCharThreshold)
const PRELOAD_BATCH_SIZE = SLOT_COUNT

const AD_HOST_KEYWORD = brandConfig.texts?.adSenseHostKeyword || ''
const hostMatch = !AD_HOST_KEYWORD || AD_HOST_KEYWORD.split(',').map(k => k.trim()).some(k => location.hostname.includes(k))

if (!hostMatch)
  console.log(TAG, `域名不包含关键字 [${AD_HOST_KEYWORD}]，不加载广告`)

let gptInited = false
const slotMap = new Map<string, GoogletagSlot>()
let nextPreloadOrder = 1
let allowedPreloadEnd = PRELOAD_BATCH_SIZE
const preloadWaiters: Array<{ order: number; resolve: () => void }> = []

function getGpt(): Googletag {
  return window.googletag as Googletag
}

function ensureGpt() {
  if (gptInited)
    return
  gptInited = true
  window.googletag = window.googletag || { cmd: [] }
  getGpt().cmd.push(() => {
    getGpt().pubads().enableSingleRequest()
    getGpt().pubads().enableAsyncRendering()
    getGpt().enableServices()
    console.log(TAG, 'GPT 初始化完成')
  })
}

export function useGptAdManager() {
  const threshold = Number(brandConfig.texts?.adSenseCharThreshold) || 500

  function getAdSlotForIndex(index: number) {
    return GPT_AD_SLOTS[index % SLOT_COUNT]
  }

  function registerPreloadItem() {
    return nextPreloadOrder++
  }

  function waitForPreloadTurn(order: number): Promise<void> {
    if (order <= allowedPreloadEnd)
      return Promise.resolve()

    console.log(
      TAG,
      '等待预加载批次',
      `globalOrder=${order}`,
      `batch=${Math.ceil(order / PRELOAD_BATCH_SIZE)}`,
      `allowedEnd=${allowedPreloadEnd}`,
    )

    return new Promise((resolve) => {
      preloadWaiters.push({ order, resolve })
    })
  }

  function releaseNextPreloadBatch(order: number) {
    if (order % PRELOAD_BATCH_SIZE !== 0)
      return

    const nextAllowedEnd = order + PRELOAD_BATCH_SIZE
    if (nextAllowedEnd <= allowedPreloadEnd)
      return

    allowedPreloadEnd = nextAllowedEnd
    console.log(
      TAG,
      '释放下一批预加载',
      `triggerGlobalOrder=${order}`,
      `nextAllowedEnd=${allowedPreloadEnd}`,
    )

    for (let i = preloadWaiters.length - 1; i >= 0; i--) {
      const waiter = preloadWaiters[i]
      if (waiter.order > allowedPreloadEnd)
        continue
      preloadWaiters.splice(i, 1)
      waiter.resolve()
    }
  }

  function displayAd(divId: string, unitPath: string, slotName = ''): Promise<boolean> {
    if (!AD_ENABLED || !hostMatch)
      return Promise.resolve(false)

    ensureGpt()

    console.log(TAG, '预加载开始', `slot=${slotName}`, `div=${divId}`, `unit=${unitPath}`)

    return new Promise((resolve) => {
      let done = false
      let handler: ((event: any) => void) | null = null
      let timer: ReturnType<typeof setTimeout>

      const finish = (filled: boolean) => {
        if (done)
          return
        done = true
        clearTimeout(timer)
        if (handler)
          getGpt().pubads().removeEventListener('slotRenderEnded', handler)
        resolve(filled)
      }

      timer = setTimeout(() => {
        if (!done) {
          console.warn(TAG, '超时 (8s) 未填充', `slot=${slotName}`, `div=${divId}`, `unit=${unitPath}`)
          finish(false)
        }
      }, 8000)

      getGpt().cmd.push(() => {
        const slot = getGpt().defineSlot(unitPath, [...SIZE], divId)
        if (!slot) {
          if (!done) {
            console.warn(TAG, 'defineSlot 失败', `slot=${slotName}`, `div=${divId}`, `unit=${unitPath}`)
            finish(false)
          }
          return
        }
        slotMap.set(divId, slot)
        slot.addService(getGpt().pubads())

        handler = (event: any) => {
          if (event.slot.getSlotElementId() === divId && !done) {
            const filled = !event.isEmpty
            if (filled)
              console.log(TAG, '填充成功', `slot=${slotName}`, `div=${divId}`, `unit=${unitPath}`)
            else
              console.warn(TAG, '填充失败（空广告）', `slot=${slotName}`, `div=${divId}`, `unit=${unitPath}`)
            finish(filled)
          }
        }
        getGpt().pubads().addEventListener('slotRenderEnded', handler)

        getGpt().display(divId)
      })
    })
  }

  function destroyAd(divId: string) {
    const slot = slotMap.get(divId)
    if (!slot || !window.googletag)
      return

    getGpt().cmd.push(() => {
      getGpt().destroySlots([slot])
      slotMap.delete(divId)
    })
  }

  return {
    getAdSlotForIndex,
    registerPreloadItem,
    waitForPreloadTurn,
    releaseNextPreloadBatch,
    displayAd,
    destroyAd,
    AD_ENABLED,
    threshold,
    PRELOAD_BATCH_SIZE,
  }
}
